import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  generateInvitePartnerLink,
  getUser,
  isKYCApp,
  LocalizePropType,
  resetPassword,
  selectAuth,
  selectBrandSettings,
  setCustomBasicAuth,
  withTextLocalizer,
  createAuthString,
} from 'getaway-data-layer';
import { useHistory } from 'react-router-dom';
import { useFormik } from 'formik';
import ResetPasswordView from './ResetPasswordView';
import {
  GA_TITLES,
  isDashboardScreen,
  navigateTo,
  reportGAEvent,
} from '../../../utils';
import { ResetPasswordModal, ResetPasswordSchema } from '../../../validation';
import { useQuery } from '../../../hooks/useQuery';
import { recreateAuthNewPassword } from 'getaway-data-layer/src/redux/store/userData/user/actions';

interface Values {
  password: string;
}

type Props = {
  localize: LocalizePropType;
};

const ResetPasswordContainer = ({ localize }: Props) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);
  const query = useQuery();
  const brandSettings = useSelector(selectBrandSettings);
  const basicAuth = useSelector(selectAuth);
  const isPartnerNewPassword = isDashboardScreen();

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const onSubmit = ({ password }: Values) => {
    if (isPartnerNewPassword) {
      dispatch(
        generateInvitePartnerLink(
          localize,
          history,
          (res) => {
            dispatch(recreateAuthNewPassword(basicAuth, password));
            navigateTo(
              history,
              `/dashboard/tariffpartner?inviteLink=${res?.link}`,
              false
            );
          },
          password
        )
      );
    } else {
      dispatch(
        resetPassword(
          query.get('email')!,
          password.trim(),
          query.get('token')!,
          localize,
          () => {
            const basicAuth = createAuthString(
              query.get('email'),
              password.trim()
            );
            dispatch(
              setCustomBasicAuth(basicAuth, () => {
                dispatch(
                  getUser(() => {
                    navigateTo(
                      history,
                      isKYCApp() ? '/settings' : '/map',
                      false
                    );
                  })
                );
              })
            );

            reportGAEvent(GA_TITLES.AUTH.RESET_PASSWORD_SUBMITTED);
          }
        )
      );
    }
  };

  const resetPasswordFormik = useFormik({
    initialValues: ResetPasswordModal,
    validationSchema: ResetPasswordSchema(
      localize,
      brandSettings?.passwordRegex
    ),
    onSubmit,
  });

  const onClose = () => {
    if (isPartnerNewPassword) {
      history.goBack();
    } else {
      navigateTo(history, '/map');
    }
  };

  useEffect(() => {
    if (!isPartnerNewPassword && !(query.get('email') && query.get('token')))
      navigateTo(history, '/map');
  }, []);

  return (
    <ResetPasswordView
      localize={localize}
      formik={resetPasswordFormik}
      showPassword={showPassword}
      email={query?.get('email') || ''}
      onClose={onClose}
      isPartnerNewPassword={isPartnerNewPassword}
      handleClickShowPassword={handleClickShowPassword}
      handleMouseDownPassword={handleMouseDownPassword}
    />
  );
};

export default withTextLocalizer(ResetPasswordContainer);
